import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, User, Upload, Loader2, Play, Pause, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import AuthPromptDialog from "@/components/AuthPromptDialog";

interface ShowcasePost {
  id: string;
  user_id: string;
  video_url: string | null;
  image_url: string | null;
  content: string | null;
  title: string | null;
  description: string | null;
  impressed_count: number;
  created_at: string;
  challenge_id: string | null;
  challenge?: {
    id: string;
    title: string;
  } | null;
  profile: {
    name: string;
    avatar_url: string;
  };
  user_impressed: boolean;
}

const Showcase = () => {
  const { user, requireAuth, showAuthPrompt, closeAuthPrompt, promptOptions } = useRequireAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<ShowcasePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [editingPost, setEditingPost] = useState<ShowcasePost | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [deletingPostId, setDeletingPostId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement }>({});

  useEffect(() => {
    fetchPosts();
  }, [user?.id]);

  const fetchPosts = async () => {
    try {
      const { data: postsData, error } = await supabase
        .from('showcase_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch profiles separately
      const userIds = [...new Set(postsData?.map(post => post.user_id) || [])];
      const challengeIds = [...new Set(postsData?.filter(p => p.challenge_id).map(p => p.challenge_id) || [])];
      
      if (userIds.length === 0) {
        setPosts([]);
        return;
      }

      const { data: profilesData, error: profileError } = await supabase
        .from('profiles')
        .select('id, name, avatar_url')
        .in('id', userIds);

      if (profileError) {
        console.error('Error fetching profiles:', profileError);
      }

      // Fetch challenges for linked posts
      let challengesMap = new Map();
      if (challengeIds.length > 0) {
        const { data: challengesData } = await supabase
          .from('seasonal_challenges')
          .select('id, title')
          .in('id', challengeIds);
        challengesMap = new Map(challengesData?.map(c => [c.id, c]) || []);
      }

      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      // Check which posts the current user has impressed
      const postsWithImpressions = await Promise.all(
        (postsData || []).map(async (post) => {
          const { data: impression } = await supabase
            .from('showcase_impressions')
            .select('id')
            .eq('post_id', post.id)
            .eq('user_id', user?.id)
            .maybeSingle();

          const profile = profilesMap.get(post.user_id);
          const challenge = post.challenge_id ? challengesMap.get(post.challenge_id) : null;

          return {
            ...post,
            challenge,
            profile: {
              name: (profile?.name && profile.name.trim() !== '') ? profile.name : 'Anonymous',
              avatar_url: profile?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.user_id}`,
            },
            user_impressed: !!impression,
          };
        })
      );

      setPosts(postsWithImpressions);
    } catch (error: any) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async () => {
    if (!videoFile || !user) return;

    setUploading(true);
    try {
      // Upload video to storage
      const fileExt = videoFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('showcase-videos')
        .upload(fileName, videoFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('showcase-videos')
        .getPublicUrl(fileName);

      // Create post
      const { error: insertError } = await supabase
        .from('showcase_posts')
        .insert({
          user_id: user.id,
          video_url: publicUrl,
          title: title || null,
          description: description || null,
        });

      if (insertError) throw insertError;

      toast({
        title: "Success!",
        description: "Your showcase has been posted",
      });

      setUploadDialogOpen(false);
      setTitle("");
      setDescription("");
      setVideoFile(null);
      fetchPosts();
    } catch (error: any) {
      console.error('Error uploading:', error);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleImpressed = async (postId: string, currentlyImpressed: boolean) => {
    requireAuth(async () => {
      try {
        if (currentlyImpressed) {
          // Remove impression
          await supabase
            .from('showcase_impressions')
            .delete()
            .eq('post_id', postId)
            .eq('user_id', user!.id);
        } else {
          // Add impression
          await supabase
            .from('showcase_impressions')
            .insert({
              post_id: postId,
              user_id: user!.id,
            });
        }

        // Refresh posts
        fetchPosts();
      } catch (error: any) {
        console.error('Error toggling impression:', error);
        toast({
          title: "Error",
          description: "Failed to update impression",
          variant: "destructive",
        });
      }
    }, { title: "Join to Impress", description: "Sign up to show appreciation for skill showcases!" });
  };

  const handleConnect = async (userId: string) => {
    requireAuth(async () => {
      try {
        // Check if conversation exists
        const { data: existingConv, error: convError } = await supabase
          .from('conversations')
          .select('id')
          .or(`and(user1_id.eq.${user!.id},user2_id.eq.${userId}),and(user1_id.eq.${userId},user2_id.eq.${user!.id})`)
          .maybeSingle();

        if (convError) {
          console.error('Error checking conversation:', convError);
        }

        if (existingConv) {
          navigate('/chat');
          return;
        }

        // Create new conversation
        const { error } = await supabase
          .from('conversations')
          .insert({
            user1_id: user!.id,
            user2_id: userId,
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Connected! Redirecting to chat...",
        });

        navigate('/chat');
      } catch (error: any) {
        console.error('Error creating conversation:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to connect",
          variant: "destructive",
        });
      }
    }, { title: "Join to Connect", description: "Sign up to start chatting with skill sharers!" });
  };

  const toggleVideoPlay = (postId: string) => {
    const video = videoRefs.current[postId];
    if (!video) return;

    if (playingVideo === postId) {
      video.pause();
      setPlayingVideo(null);
    } else {
      // Pause all other videos
      Object.values(videoRefs.current).forEach(v => v.pause());
      video.play();
      setPlayingVideo(postId);
    }
  };

  const handleEditPost = (post: ShowcasePost) => {
    setEditingPost(post);
    setEditContent(post.content || post.description || "");
    setEditTitle(post.title || "");
  };

  const handleUpdatePost = async () => {
    if (!editingPost || !user) return;
    
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('showcase_posts')
        .update({
          content: editContent.trim() || null,
          title: editTitle.trim() || null,
          description: editContent.trim() || null,
        })
        .eq('id', editingPost.id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Updated!",
        description: "Your post has been updated",
      });
      
      setEditingPost(null);
      fetchPosts();
    } catch (error: any) {
      console.error('Error updating post:', error);
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeletePost = async () => {
    if (!deletingPostId || !user) return;

    try {
      const { error } = await supabase
        .from('showcase_posts')
        .delete()
        .eq('id', deletingPostId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Deleted",
        description: "Your post has been deleted",
      });
      
      setDeletingPostId(null);
      fetchPosts();
    } catch (error: any) {
      console.error('Error deleting post:', error);
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Auth Prompt Dialog */}
      <AuthPromptDialog 
        open={showAuthPrompt} 
        onOpenChange={closeAuthPrompt}
        title={promptOptions.title}
        description={promptOptions.description}
      />
      
      {/* Header */}
      <header className="gradient-primary text-white p-6 pb-8 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
            <Play className="h-6 w-6" />
            Showcase
          </h1>
          <p className="text-white/90 text-sm">Impress & Connect</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container max-w-md mx-auto px-4 -mt-4 space-y-4">
        {/* Upload Button */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="w-full shadow-soft"
              onClick={(e) => {
                if (!user) {
                  e.preventDefault();
                  requireAuth(() => setUploadDialogOpen(true), {
                    title: "Join to Share",
                    description: "Sign up to showcase your skills and impress others!"
                  });
                }
              }}
            >
              <Upload className="mr-2 h-4 w-4" />
              Post Your Skill
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Showcase Video</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Video (max 30 seconds)</label>
                <Input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Check file size (50MB max)
                      if (file.size > 52428800) {
                        toast({
                          title: "File too large",
                          description: "Maximum file size is 50MB",
                          variant: "destructive",
                        });
                        return;
                      }
                      setVideoFile(file);
                    }
                  }}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Title (optional)</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your showcase a title..."
                  maxLength={100}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Description (optional)</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your skill..."
                  maxLength={500}
                  rows={3}
                />
              </div>
              <Button
                className="w-full"
                onClick={handleVideoUpload}
                disabled={!videoFile || uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Posts Feed */}
        <div className="space-y-4">
          {posts.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">No showcases yet. Be the first to post!</p>
            </Card>
          ) : (
            posts.map((post) => (
              <Card key={post.id} className="overflow-hidden shadow-soft">
                {/* User Header */}
                <div className="p-4 flex items-center gap-3">
                  <Avatar 
                    className="cursor-pointer"
                    onClick={() => navigate(`/profile?id=${post.user_id}`)}
                  >
                    <AvatarImage 
                      src={post.profile?.avatar_url} 
                      alt={post.profile?.name || 'User'}
                    />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {(post.profile?.name || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    {post.challenge ? (
                      <p className="text-sm">
                        <span 
                          className="font-semibold cursor-pointer hover:text-primary transition-colors"
                          onClick={() => navigate(`/profile?id=${post.user_id}`)}
                        >
                          {post.profile?.name || 'Anonymous'}
                        </span>
                        <span className="text-muted-foreground"> took part in </span>
                        <span 
                          className="font-semibold text-primary cursor-pointer hover:underline"
                          onClick={() => navigate('/challenges')}
                        >
                          {post.challenge.title}
                        </span>
                      </p>
                    ) : (
                      <>
                        <p 
                          className="font-semibold cursor-pointer hover:text-primary transition-colors"
                          onClick={() => navigate(`/profile?id=${post.user_id}`)}
                        >
                          {post.profile?.name || 'Anonymous'}
                        </p>
                        {post.title && (
                          <p className="text-sm text-muted-foreground">{post.title}</p>
                        )}
                      </>
                    )}
                  </div>
                  {/* Edit/Delete Menu for post owner */}
                  {post.user_id === user?.id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditPost(post)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setDeletingPostId(post.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* Media Content */}
                {post.video_url && (
                  <div className="relative bg-black aspect-video">
                    <video
                      ref={(el) => {
                        if (el) videoRefs.current[post.id] = el;
                      }}
                      src={post.video_url}
                      className="w-full h-full object-contain"
                      loop
                      playsInline
                      onClick={() => toggleVideoPlay(post.id)}
                    />
                    <button
                      onClick={() => toggleVideoPlay(post.id)}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                    >
                      {playingVideo === post.id ? (
                        <Pause className="h-16 w-16 text-white" />
                      ) : (
                        <Play className="h-16 w-16 text-white" />
                      )}
                    </button>
                  </div>
                )}

                {post.image_url && !post.video_url && (
                  <div className="bg-muted">
                    <img 
                      src={post.image_url} 
                      alt="Post" 
                      className="w-full max-h-96 object-contain"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="p-4 space-y-3">
                  {(post.content || post.description) && (
                    <p className="text-sm">{post.content || post.description}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleImpressed(post.id, post.user_impressed)}
                      className="flex-1"
                    >
                      <Heart className={`mr-2 h-4 w-4 ${post.user_impressed ? 'fill-current' : ''}`} />
                      Impressed ({post.impressed_count})
                    </Button>
                    {post.user_id !== user?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConnect(post.user_id)}
                        className="flex-1"
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Connect
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={!!editingPost} onOpenChange={(open) => !open && setEditingPost(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Title (optional)</label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Give your post a title..."
                maxLength={100}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="Describe your skill..."
                maxLength={500}
                rows={4}
              />
            </div>
            <Button
              className="w-full"
              onClick={handleUpdatePost}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingPostId} onOpenChange={(open) => !open && setDeletingPostId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Your post will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePost} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Navigation />
    </div>
  );
};

export default Showcase;
